import { Box } from '@mui/material';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import ListHeader from '../components/listHeader/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { FilterState } from '../components/common/filters/interfaces';
import PreferencePill from '../components/preferences/PreferencePill';
import { RolePillsCell } from '../components/preferences/RolePillsCell';
import { useDownloadCsv } from '../hooks/useDownloadCsv';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { ContactAvailableColumns, ContactSortParam, useGetAllContacts } from '../services/react-query/contacts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetAllContactTemplates } from '../services/react-query/contact_templates';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { Contact } from '../types/Contact';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { ContactColumns, ContactFilters } from '../utility/contact';
import ListHeaderActions from '../components/listHeader/ListHeaderActions';
import TemplateModal from '../components/common/TemplateModal/TemplateModal';
import { useNavigate } from 'react-router-dom';
import qs from 'qs';

const ContactsList: React.FC = () => {
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { userSorts } = useUserSorts<ContactSortParam>(UserConfigurableModule.CONTACTS);
  const [showModal, setShowModal] = useState(false);

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE, PREFERENCES_VALID_KEYS.CONTACT_TITLE] },
    },
  });

  const { data: templates } = useGetAllContactTemplates({
    params: { per_page: 0 },
  });

  const contactTemplates = templates?.data ?? [];

  const prefByUuid = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const { data, isLoading: loading } = useGetAllContacts({
    params: {
      ...baseParams,
      page: currentPage,
    },
    enabled: true,
  });

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.CONTACTS,
  });

  const newWithTemplate = (templateUuid: string | null) => {
    const params: any = {};
    if (templateUuid) params.template = templateUuid;

    navigate(`./new?${qs.stringify(params)}`);
  };

  if (loading) return <Loading />;

  const contacts = data?.data || [];
  const meta = data?.meta;

  const pillColumns: AvailableColumn<Contact, ContactAvailableColumns>[] = ContactColumns.map((col) => {
    if (col.key === 'contact_title_value') {
      return {
        ...col,
        render: (row: Contact) => {
          return <PreferencePill {...prefByUuid[row.contact_title_uuid ?? '']} />;
        },
      };
    }

    if (col.key === 'roles') {
      return {
        ...col,
        sortable: false,
        render: (row: Contact) => {
          const rolesUuids = _.isArray(row.roles_uuids) ? row.roles_uuids : [];
          return <RolePillsCell rolesUuids={rolesUuids} prefByUuid={prefByUuid} />;
        },
      };
    }

    return col;
  });

  const availableColumns = ContactColumns.map(({ key, label }) => ({
    key,
    label,
  }));

  return (
    <Box>
      <ListHeader
        title="Contacts"
        rightActions={
          <ListHeaderActions
            module={UserConfigurableModule.CONTACTS}
            availableColumns={availableColumns}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onAdd={() => setShowModal(true)}
          />
        }
      />

      <Box sx={listPageLayoutStyles}>
        <FilterSidebar
          moduleFilters={ContactFilters(preferenceData)}
          module={UserConfigurableModule.CONTACTS}
          activeFilter={filterState}
          setActiveFilter={setFilterState}
        />

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Contact, ContactAvailableColumns>
            availableColumns={pillColumns}
            data={contacts}
            module={UserConfigurableModule.CONTACTS}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={newWithTemplate}
        templates={contactTemplates}
        title="Please choose which contact template to use"
      />
    </Box>
  );
};

export default ContactsList;
